import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { useMasterOptionSearch, type MasterOptionKind } from '@/hooks/useMasterOptionSearch';

export interface MasterDataSelectProps {
  kind: MasterOptionKind;
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  hint?: string;
  className?: string;
  name?: string;
}

function defaults(kind: MasterOptionKind) {
  switch (kind) {
    case 'countries':
      return {
        label: 'Country',
        placeholder: 'Select country',
        searchPlaceholder: 'Search countries…',
        emptyMessage: 'No countries found',
      };
    case 'currencies':
      return {
        label: 'Currency',
        placeholder: 'Select currency',
        searchPlaceholder: 'Search currencies…',
        emptyMessage: 'No currencies found',
      };
    case 'timezones':
      return {
        label: 'Timezone',
        placeholder: 'Select timezone',
        searchPlaceholder: 'Search timezones…',
        emptyMessage: 'No timezones found',
      };
  }
}

export function MasterDataSelect({
  kind,
  label,
  value = '',
  onChange,
  placeholder,
  disabled,
  required,
  error,
  hint,
  className,
  name,
}: MasterDataSelectProps) {
  const copy = defaults(kind);
  const { options, loading, searching, onSearchQuery } = useMasterOptionSearch(kind, value);

  return (
    <SearchableSelect
      label={label ?? copy.label}
      name={name}
      required={required}
      error={error}
      hint={hint}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder ?? copy.placeholder}
      disabled={disabled}
      loading={loading}
      searching={searching}
      className={className}
      searchPlaceholder={copy.searchPlaceholder}
      emptyMessage={copy.emptyMessage}
      filterLocally={false}
      onSearchQuery={onSearchQuery}
    />
  );
}

export function CountrySelect(props: Omit<MasterDataSelectProps, 'kind'>) {
  return <MasterDataSelect kind="countries" {...props} />;
}

export function CurrencySelect(props: Omit<MasterDataSelectProps, 'kind'>) {
  return <MasterDataSelect kind="currencies" {...props} />;
}

export function TimezoneSelect(props: Omit<MasterDataSelectProps, 'kind'>) {
  return <MasterDataSelect kind="timezones" {...props} />;
}
